import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Switch, FormControlLabel, CircularProgress, Chip, Collapse, Divider,
} from '@mui/material';
import {
  PlusIcon, PencilSquareIcon, TrashIcon, ChevronDownIcon, ArrowLeftIcon,
  ClockIcon, ReceiptPercentIcon, BanknotesIcon, TicketIcon, CheckBadgeIcon,
  StarIcon, TrophyIcon, CakeIcon, GiftIcon, SparklesIcon, Squares2X2Icon,
  ArrowTrendingUpIcon, ArrowUturnLeftIcon, UserPlusIcon, HandRaisedIcon,
  ArrowPathIcon, CreditCardIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  PageHeroBand, PrimaryButton, SecondaryButton, AppIconButton, EmptyState, ConfirmDialog,
} from '../components/ui';
import {
  getPromotions, createPromotion, updatePromotion, togglePromotion, deletePromotion,
  getPromotionMenuItems, getVouchers, getVoucherLiability, issueVoucher,
  getVoucherHistory, deactivateVoucher,
  type Promotion, type PromotionInput, type PromoType, type PromotionMenuItem,
  type Voucher, type VoucherTxn,
} from '../api/api';
import { CURRENCY, formatCurrency } from '../config/currency';
import { useVendorConfig } from '../context/VendorConfigContext';

/**
 * Loyalty Programs — every offer a restaurant runs, in one place.
 *
 * WHY ONE PAGE
 *
 * An operator does not think "this is a promotion and that is a loyalty
 * programme". They think "these are the offers I run". A happy hour, a stamp
 * card and a birthday treat differ in when they fire, not in what they are, so
 * splitting them across two pages made the customer explain a distinction that
 * only existed in the database. One table (`promotions`) now backs all of them,
 * and this page is the single front door.
 *
 * HOW THE FORM WORKS
 *
 * Choosing the KIND comes first, on its own step, as a grid of tiles. Everything
 * after that is decided by the tile: a happy hour asks for times, a stamp card
 * asks how many visits, a points scheme asks what a point is worth. Nobody is
 * shown a field that does not apply to what they picked.
 *
 * The eight most-used kinds are shown; the rest sit behind "More kinds" rather
 * than being left out. A restaurant that wants a win-back campaign should not
 * have to ask for it, but a restaurant setting up its first happy hour should
 * not have to read past it either.
 *
 * WHAT IT DOES NOT DO
 *
 * Nothing on this page changes a bill. Creating an offer and having it come off
 * a total are separate steps by design — this page can be exercised end to end
 * without money moving. Wiring the discount into billing is deliberately held
 * back until the till path is decided.
 *
 * COUNTRY
 *
 * Currency comes from the signed-in vendor, so the same page reads correctly in
 * either market. Two rules genuinely differ and are handled where they apply:
 * the RBI cap on an Indian gift voucher, and the point at which UK VAT falls due
 * on a gift card. Both live in the vouchers section, since that is the only
 * place money is taken up front.
 */

// ── the catalogue ───────────────────────────────────────────────────────
// One entry per tile. `needs` decides which fields the form shows, so adding a
// kind is a matter of describing it here rather than editing the form.

type FieldNeed =
  | 'percentOrAmount' | 'percentOnly' | 'amountOrPercent'
  | 'window' | 'schedule' | 'minSpend' | 'cap'
  | 'visits' | 'spendTarget' | 'itemCount' | 'qualifyingItem' | 'freeItem'
  | 'points' | 'minRedeem' | 'birthdayWindow' | 'lapsedWindow'
  | 'validity' | 'coupon' | 'advanced';

interface OfferKind {
  key: string;                     // tile identity; usually the promo type
  promoType: PromoType;
  label: string;
  blurb: string;
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
  common: boolean;                 // shown up front, or behind "More kinds"
  group: 'till' | 'earned' | 'event';
  needs: FieldNeed[];
  /** Applied when the tile is chosen for a NEW offer. */
  preset?: Partial<Draft>;
  namePlaceholder: string;
}

const KINDS: OfferKind[] = [
  {
    key: 'HAPPY_HOUR', promoType: 'HAPPY_HOUR', label: 'Happy hour',
    blurb: 'Runs between set times on chosen days', icon: ClockIcon,
    common: true, group: 'till',
    needs: ['percentOrAmount', 'window', 'schedule', 'minSpend', 'cap', 'coupon', 'advanced'],
    preset: { startTime: '16:00', endTime: '18:00' },
    namePlaceholder: 'e.g. Weekday happy hour',
  },
  {
    key: 'PERCENT_OFF', promoType: 'PERCENT_OFF', label: 'Percentage off',
    blurb: 'A share off the bill', icon: ReceiptPercentIcon,
    common: true, group: 'till',
    needs: ['percentOnly', 'schedule', 'minSpend', 'cap', 'coupon', 'advanced'],
    preset: { valueMode: 'percentage' },
    namePlaceholder: 'e.g. 10% off everything',
  },
  {
    key: 'FLAT_OFF', promoType: 'FLAT_OFF', label: 'Amount off',
    blurb: `A fixed ${CURRENCY.symbol} amount off the bill`, icon: BanknotesIcon,
    common: true, group: 'till',
    needs: ['percentOrAmount', 'schedule', 'minSpend', 'coupon', 'advanced'],
    preset: { valueMode: 'amount' },
    namePlaceholder: `e.g. ${CURRENCY.symbol}100 off`,
  },
  {
    // Not a type of its own — a coupon is any offer with a code on it. The tile
    // exists because operators ask for "a coupon", and starting them on a
    // percentage with the code box already open is the shortest honest route.
    key: 'COUPON', promoType: 'PERCENT_OFF', label: 'Coupon code',
    blurb: 'Only applies when the guest gives a code', icon: TicketIcon,
    common: true, group: 'till',
    needs: ['percentOrAmount', 'schedule', 'minSpend', 'cap', 'coupon', 'advanced'],
    preset: { valueMode: 'percentage', useCode: true, code: '' },
    namePlaceholder: 'e.g. Welcome back voucher',
  },
  {
    key: 'STAMP_CARD', promoType: 'STAMP_CARD', label: 'Stamp card',
    blurb: 'Every Nth visit earns a reward', icon: CheckBadgeIcon,
    common: true, group: 'earned',
    needs: ['visits', 'percentOrAmount', 'freeItem', 'validity', 'minSpend'],
    preset: { requiredVisits: '10' },
    namePlaceholder: 'e.g. Tenth coffee free',
  },
  {
    key: 'POINTS', promoType: 'POINTS', label: 'Points',
    blurb: 'Earn points on every bill, spend them later', icon: StarIcon,
    common: true, group: 'earned',
    needs: ['points', 'minRedeem', 'minSpend'],
    preset: { pointsPerUnit: '1', pointsToValue: '0.1' },
    namePlaceholder: 'e.g. Regulars points',
  },
  {
    key: 'TIER', promoType: 'TIER', label: 'Tiers',
    blurb: 'Reach a lifetime spend, keep the benefit', icon: TrophyIcon,
    common: true, group: 'earned',
    needs: ['spendTarget', 'percentOrAmount'],
    preset: { valueMode: 'percentage' },
    namePlaceholder: 'e.g. Gold member',
  },
  {
    key: 'BIRTHDAY', promoType: 'BIRTHDAY', label: 'Birthday treat',
    blurb: 'Fires on the guest’s birthday', icon: CakeIcon,
    common: true, group: 'event',
    needs: ['percentOrAmount', 'freeItem', 'birthdayWindow', 'minSpend', 'coupon'],
    preset: { triggerWindowDays: '15' },
    namePlaceholder: 'e.g. Birthday dessert',
  },

  // ── behind "More kinds" ───────────────────────────────────────────────
  {
    key: 'BOGO', promoType: 'BOGO', label: 'Buy one, get one',
    blurb: 'Buy a number of an item, get one free', icon: GiftIcon,
    common: false, group: 'till',
    needs: ['qualifyingItem', 'itemCount', 'freeItem', 'schedule', 'coupon', 'advanced'],
    preset: { requiredItemCount: '1' },
    namePlaceholder: 'e.g. BOGO on starters',
  },
  {
    key: 'ITEM_FREE', promoType: 'ITEM_FREE', label: 'Free item',
    blurb: 'One dish costs nothing', icon: SparklesIcon,
    common: false, group: 'till',
    needs: ['freeItem', 'schedule', 'minSpend', 'coupon', 'advanced'],
    namePlaceholder: 'e.g. Free naan over ' + CURRENCY.symbol + '500',
  },
  {
    key: 'COMBO', promoType: 'COMBO', label: 'Combo price',
    blurb: 'A set of dishes at one price', icon: Squares2X2Icon,
    common: false, group: 'till',
    needs: ['amountOrPercent', 'schedule', 'coupon', 'advanced'],
    preset: { valueMode: 'amount' },
    namePlaceholder: 'e.g. Lunch thali deal',
  },
  {
    key: 'SPEND_GOAL', promoType: 'SPEND_GOAL', label: 'Spend goal',
    blurb: 'Spend a total, then get the reward once', icon: ArrowTrendingUpIcon,
    common: false, group: 'earned',
    needs: ['spendTarget', 'percentOrAmount', 'freeItem', 'validity'],
    namePlaceholder: `e.g. Spend ${CURRENCY.symbol}5,000`,
  },
  {
    key: 'CASHBACK', promoType: 'CASHBACK', label: 'Cashback',
    blurb: 'A share of the bill back as credit', icon: ArrowUturnLeftIcon,
    common: false, group: 'earned',
    needs: ['percentOnly', 'cap', 'minSpend', 'minRedeem'],
    preset: { valueMode: 'percentage' },
    namePlaceholder: 'e.g. 5% back',
  },
  {
    key: 'REFERRAL', promoType: 'REFERRAL', label: 'Referral',
    blurb: 'Rewards a guest who brings someone new', icon: UserPlusIcon,
    common: false, group: 'event',
    needs: ['percentOrAmount', 'freeItem', 'coupon'],
    namePlaceholder: 'e.g. Bring a friend',
  },
  {
    key: 'WELCOME', promoType: 'WELCOME', label: 'Welcome offer',
    blurb: 'For a guest’s first order after signing up', icon: HandRaisedIcon,
    common: false, group: 'event',
    needs: ['percentOrAmount', 'freeItem', 'minSpend', 'coupon'],
    namePlaceholder: 'e.g. First order discount',
  },
  {
    key: 'WIN_BACK', promoType: 'WIN_BACK', label: 'Win back',
    blurb: 'For someone who has not been in for a while', icon: ArrowPathIcon,
    common: false, group: 'event',
    needs: ['percentOrAmount', 'freeItem', 'lapsedWindow', 'coupon'],
    preset: { triggerWindowDays: '60' },
    namePlaceholder: 'e.g. We miss you',
  },
];

const kindFor = (p: Promotion): OfferKind => {
  // A coupon is any offer carrying a code, so it is matched before the type.
  if (p.isCoupon && p.promoType === 'PERCENT_OFF') {
    return KINDS.find(k => k.key === 'COUPON')!;
  }
  return KINDS.find(k => k.promoType === p.promoType) ?? KINDS[0];
};

const GROUP_LABEL: Record<OfferKind['group'], string> = {
  till: 'Applies at the till',
  earned: 'Earned over repeat visits',
  event: 'Fires when something happens',
};

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;

const CHANNELS = [
  { value: 'TABLE_SERVICE', label: 'Dine-in' },
  { value: 'TAKEAWAY', label: 'Takeaway' },
  { value: 'DELIVERY', label: 'Delivery' },
  { value: 'COUNTER_SERVICE', label: 'Counter' },
];

// ── draft ───────────────────────────────────────────────────────────────
// Everything is a string while it is being typed. Numbers are parsed once, on
// save, so a half-typed "1." never becomes NaN in state.

type Draft = {
  id?: number;
  kindKey: string;
  name: string;
  description: string;
  valueMode: 'percentage' | 'amount';
  value: string;
  maxDiscountAmount: string;
  minOrderAmount: string;
  startTime: string;
  endTime: string;
  startsOn: string;
  endsOn: string;
  days: string[];
  channels: string[];
  useCode: boolean;
  code: string;
  maxRedemptions: string;
  maxPerCustomer: string;
  requiredVisits: string;
  requiredSpend: string;
  requiredItemCount: string;
  qualifyingItemId: string;
  freeItemId: string;
  pointsPerUnit: string;
  pointsToValue: string;
  minRedeemPoints: string;
  triggerWindowDays: string;
  rewardValidityDays: string;
  nonExclusive: boolean;
  priority: string;
};

const emptyDraft: Draft = {
  kindKey: 'HAPPY_HOUR',
  name: '', description: '',
  valueMode: 'percentage', value: '',
  maxDiscountAmount: '', minOrderAmount: '',
  startTime: '', endTime: '', startsOn: '', endsOn: '',
  days: [], channels: [],
  useCode: false, code: '', maxRedemptions: '', maxPerCustomer: '',
  requiredVisits: '', requiredSpend: '', requiredItemCount: '', minRedeemPoints: '',
  triggerWindowDays: '',
  qualifyingItemId: '', freeItemId: '',
  pointsPerUnit: '', pointsToValue: '', rewardValidityDays: '',
  nonExclusive: false, priority: '100',
};

const cardSx = {
  p: { xs: 1.5, sm: 2 },
  borderRadius: '12px',
  backgroundColor: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
};

const labelSx = {
  fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' as const,
  letterSpacing: '0.05em', color: 'var(--text-tertiary)', mb: 1,
};

/**
 * The sentence the API sent back, if it sent one.
 *
 * The promotion endpoints answer a rejection with a message naming the missing
 * field in the same words this page uses for it, so showing it beats replacing
 * it with something vaguer.
 */
const apiMessage = (err: unknown, fallback: string): string => {
  const response = (err as { response?: { data?: { message?: unknown } } })?.response;
  const message = response?.data?.message;
  return typeof message === 'string' && message ? message : fallback;
};

/** "2026-08-16" -> "16 Aug". Falls back to the raw value rather than guessing. */
const shortDate = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const num = (s: string): number | null => {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

const VendorLoyaltyPrograms: React.FC = () => {
  const [tab, setTab] = useState<'offers' | 'vouchers'>('offers');

  // The same flag Business Configuration shows. Deliberately reachable from
  // both: someone setting up a happy hour is standing on THIS page, and sending
  // them to a settings screen to answer "does it apply by itself?" is a worse
  // journey than putting the switch beside the offers. One value underneath, so
  // the two can never disagree.
  const { configuration, updateConfiguration } = useVendorConfig();
  const [savingAutoApply, setSavingAutoApply] = useState(false);
  const autoApply = configuration?.autoApplyOffers ?? true;

  const toggleAutoApply = async () => {
    setSavingAutoApply(true);
    try {
      await updateConfiguration({ autoApplyOffers: !autoApply });
      toast.success(autoApply
        ? 'Offers will now be shown to staff to confirm'
        : 'Offers will now apply on their own');
    } catch {
      toast.error('Could not change that');
    } finally {
      setSavingAutoApply(false);
    }
  };

  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [menuItems, setMenuItems] = useState<PromotionMenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Two steps: pick the kind, then fill it in. `step` decides which is showing.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState<'pick' | 'form'>('pick');
  const [showMoreKinds, setShowMoreKinds] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [confirmDelete, setConfirmDelete] = useState<Promotion | null>(null);

  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [liability, setLiability] = useState(0);
  const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState<Voucher | null>(null);
  const [history, setHistory] = useState<VoucherTxn[]>([]);
  const [confirmDeactivate, setConfirmDeactivate] = useState<Voucher | null>(null);

  const isIndia = CURRENCY.code === 'INR';

  const load = useCallback(async () => {
    try {
      const [promos, items] = await Promise.all([getPromotions(), getPromotionMenuItems()]);
      setPromotions(promos.data);
      setMenuItems(items.data);
    } catch {
      toast.error('Could not load offers');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadVouchers = useCallback(async () => {
    try {
      const [list, owed] = await Promise.all([getVouchers(), getVoucherLiability()]);
      setVouchers(list.data);
      setLiability(Number(owed.data.outstanding) || 0);
    } catch {
      toast.error('Could not load gift cards');
    }
  }, []);

  useEffect(() => { load(); loadVouchers(); }, [load, loadVouchers]);

  const liveCount = useMemo(() => promotions.filter(p => p.liveNow).length, [promotions]);
  const activeCount = useMemo(() => promotions.filter(p => p.isActive).length, [promotions]);

  const kind = useMemo(
    () => KINDS.find(k => k.key === draft.kindKey) ?? KINDS[0],
    [draft.kindKey],
  );
  const needs = (f: FieldNeed) => kind.needs.includes(f);

  /**
   * Whether to show the code box.
   *
   * Normally the kind decides. The extra clause covers an offer that already
   * HAS a code on a kind that does not offer one — the API permits that, and
   * without this the box would be hidden and the next save would silently strip
   * the code off a live coupon.
   */
  const showsCoupon = needs('coupon') || draft.useCode;

  // ── create / edit ─────────────────────────────────────────────────────

  const openNew = () => {
    setDraft(emptyDraft);
    setStep('pick');
    setShowMoreKinds(false);
    setShowAdvanced(false);
    setDialogOpen(true);
  };

  const chooseKind = (k: OfferKind) => {
    setDraft({ ...emptyDraft, kindKey: k.key, ...k.preset });
    setStep('form');
  };

  const openEdit = (p: Promotion) => {
    const k = kindFor(p);
    setDraft({
      id: p.id,
      kindKey: k.key,
      name: p.name,
      description: p.description ?? '',
      valueMode: p.amount != null ? 'amount' : 'percentage',
      value: String(p.percentage ?? p.amount ?? ''),
      maxDiscountAmount: p.maxDiscountAmount != null ? String(p.maxDiscountAmount) : '',
      minOrderAmount: p.minOrderAmount != null ? String(p.minOrderAmount) : '',
      startTime: p.startTime ? p.startTime.slice(0, 5) : '',
      endTime: p.endTime ? p.endTime.slice(0, 5) : '',
      startsOn: p.startsOn ?? '',
      endsOn: p.endsOn ?? '',
      days: p.daysOfWeek ? p.daysOfWeek.split(',') : [],
      channels: p.channels ? p.channels.split(',') : [],
      useCode: !!p.code,
      code: p.code ?? '',
      maxRedemptions: p.maxRedemptions != null ? String(p.maxRedemptions) : '',
      maxPerCustomer: p.maxPerCustomer != null ? String(p.maxPerCustomer) : '',
      requiredVisits: p.requiredVisits != null ? String(p.requiredVisits) : '',
      minRedeemPoints: p.minRedeemPoints != null ? String(p.minRedeemPoints) : '',
      triggerWindowDays: p.triggerWindowDays != null ? String(p.triggerWindowDays) : '',
      requiredSpend: p.requiredSpend != null ? String(p.requiredSpend) : '',
      requiredItemCount: p.requiredItemCount != null ? String(p.requiredItemCount) : '',
      qualifyingItemId: p.qualifyingItemId != null ? String(p.qualifyingItemId) : '',
      freeItemId: p.freeItemId != null ? String(p.freeItemId) : '',
      pointsPerUnit: p.pointsPerUnit != null ? String(p.pointsPerUnit) : '',
      pointsToValue: p.pointsToValue != null ? String(p.pointsToValue) : '',
      rewardValidityDays: p.rewardValidityDays != null ? String(p.rewardValidityDays) : '',
      nonExclusive: !!p.nonExclusive,
      priority: String(p.priority ?? 100),
    });
    setStep('form');
    setShowAdvanced(false);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!draft.name.trim()) { toast.error('Give the offer a name'); return; }

    const wantsValue = needs('percentOrAmount') || needs('percentOnly') || needs('amountOrPercent');
    const mode = needs('percentOnly') ? 'percentage' : draft.valueMode;
    const value = num(draft.value);

    // Only send a code when the switch is on. The backend clears the code when
    // it receives a blank one, so this is the single place that decides whether
    // an offer stays a coupon.
    const useCode = draft.useCode;
    if (useCode && !draft.code.trim()) { toast.error('Give the coupon a code'); return; }

    const payload: PromotionInput = {
      name: draft.name.trim(),
      description: draft.description.trim() || null,
      promoType: kind.promoType,
      percentage: wantsValue && mode === 'percentage' ? value : null,
      amount: wantsValue && mode === 'amount' ? value : null,
      maxDiscountAmount: needs('cap') ? num(draft.maxDiscountAmount) : null,
      minOrderAmount: needs('minSpend') ? num(draft.minOrderAmount) : null,
      startTime: needs('window') && draft.startTime ? draft.startTime : null,
      endTime: needs('window') && draft.endTime ? draft.endTime : null,
      startsOn: draft.startsOn || null,
      endsOn: draft.endsOn || null,
      daysOfWeek: needs('schedule') && draft.days.length ? draft.days.join(',') : null,
      channels: needs('schedule') && draft.channels.length ? draft.channels.join(',') : null,
      code: useCode ? draft.code.trim().toUpperCase() : null,
      maxRedemptions: useCode ? num(draft.maxRedemptions) : null,
      maxPerCustomer: useCode ? num(draft.maxPerCustomer) : null,
      requiredVisits: needs('visits') ? num(draft.requiredVisits) : null,
      requiredSpend: needs('spendTarget') ? num(draft.requiredSpend) : null,
      requiredItemCount: needs('itemCount') ? num(draft.requiredItemCount) : null,
      qualifyingItemId: needs('qualifyingItem') ? num(draft.qualifyingItemId) : null,
      freeItemId: needs('freeItem') ? num(draft.freeItemId) : null,
      pointsPerUnit: needs('points') ? num(draft.pointsPerUnit) : null,
      pointsToValue: needs('points') ? num(draft.pointsToValue) : null,
      minRedeemPoints: needs('minRedeem') ? num(draft.minRedeemPoints) : null,
      triggerWindowDays: (needs('birthdayWindow') || needs('lapsedWindow'))
        ? num(draft.triggerWindowDays) : null,
      rewardValidityDays: needs('validity') ? num(draft.rewardValidityDays) : null,
      nonExclusive: draft.nonExclusive,
      priority: num(draft.priority) ?? 100,
    };

    setSaving(true);
    try {
      if (draft.id) {
        await updatePromotion(draft.id, payload);
        toast.success('Offer updated');
      } else {
        await createPromotion(payload);
        toast.success('Offer created');
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(apiMessage(err, 'Could not save the offer'));
    } finally {
      setSaving(false);
    }
  };

  const onToggle = async (p: Promotion) => {
    try { await togglePromotion(p.id); await load(); }
    catch { toast.error('Could not change the offer'); }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deletePromotion(confirmDelete.id);
      toast.success('Offer deleted');
      setConfirmDelete(null);
      await load();
    } catch { toast.error('Could not delete the offer'); }
  };

  /**
   * Why an offer that looks set up is not running — when the reason is its dates.
   *
   * Only the date range is judged here, and only at day granularity, so the
   * browser's clock cannot contradict the server: "starts 16 Aug" is true
   * wherever you read it. Whether it is live RIGHT NOW stays the server's answer,
   * because that depends on the restaurant's timezone.
   */
  const scheduleNote = (p: Promotion): string | null => {
    const today = new Date().toISOString().slice(0, 10);
    if (p.startsOn && p.startsOn > today) return `Starts ${shortDate(p.startsOn)}`;
    if (p.endsOn && p.endsOn < today) return `Ended ${shortDate(p.endsOn)}`;
    return null;
  };

  /** One line under the offer name saying what it actually does. */
  const describe = (p: Promotion): string => {
    const money = (n: number | null) => (n == null ? '' : formatCurrency(n));
    const reward = p.percentage != null ? `${p.percentage}% off`
      : p.amount != null ? `${money(p.amount)} off`
      : p.freeItemName ? `${p.freeItemName} free`
      : 'a reward';

    const parts: string[] = [];
    switch (p.promoType) {
      case 'STAMP_CARD':
        parts.push(`${reward} on visit ${p.requiredVisits ?? '?'}`);
        break;
      case 'POINTS':
        parts.push(`${p.pointsPerUnit ?? '?'} point per ${CURRENCY.symbol}1`);
        if (p.pointsToValue != null) parts.push(`1 point = ${money(p.pointsToValue)}`);
        if (p.minRedeemPoints != null) parts.push(`${p.minRedeemPoints}+ at a time`);
        break;
      case 'SPEND_GOAL':
        parts.push(`${reward} after ${money(p.requiredSpend)} spent`);
        break;
      case 'TIER':
        // "Kept from then on" is now literally true: reaching the threshold
        // applies the benefit to every bill without anybody pressing anything.
        parts.push(`${reward} on every bill once ${money(p.requiredSpend)} spent`);
        break;
      case 'CASHBACK':
        parts.push(`${p.percentage ?? '?'}% back as credit to spend next time`);
        break;
      case 'BOGO':
        parts.push(`buy ${p.requiredItemCount ?? '?'} × ${p.qualifyingItemName ?? 'item'}, get one free`);
        break;
      case 'ITEM_FREE':
        parts.push(`${p.freeItemName ?? 'an item'} free`);
        break;
      case 'BIRTHDAY':
      case 'REFERRAL':
      case 'WELCOME':
      case 'WIN_BACK':
        parts.push(reward);
        break;
      default:
        parts.push(reward);
    }

    if (p.startTime && p.endTime) {
      parts.push(`${p.startTime.slice(0, 5)}–${p.endTime.slice(0, 5)}`);
    }
    if (p.daysOfWeek) parts.push(p.daysOfWeek.split(',').join(' '));
    if (p.channels) {
      parts.push(p.channels.split(',')
        .map(c => CHANNELS.find(x => x.value === c)?.label ?? c).join(', '));
    }
    if (p.minOrderAmount != null) parts.push(`over ${money(p.minOrderAmount)}`);

    // The campaign dates, which were previously left out entirely. An offer can
    // be switched on, inside its hours and on the right day, and still not be
    // running because it starts next week — and without this the row gave no
    // way at all to see that.
    if (p.startsOn && p.endsOn) {
      parts.push(`${shortDate(p.startsOn)} – ${shortDate(p.endsOn)}`);
    } else if (p.startsOn) {
      parts.push(`from ${shortDate(p.startsOn)}`);
    } else if (p.endsOn) {
      parts.push(`until ${shortDate(p.endsOn)}`);
    }

    if (p.code) {
      const used = p.maxRedemptions != null
        ? `${p.redemptionsUsed}/${p.maxRedemptions} used`
        : `${p.redemptionsUsed} used`;
      parts.push(`code ${p.code} · ${used}`);
    }
    return parts.join(' · ');
  };

  // ── vouchers ──────────────────────────────────────────────────────────

  const [voucherDraft, setVoucherDraft] = useState({
    voucherType: 'GIFT_CARD' as 'GIFT_CARD' | 'PROMO_CREDIT',
    faceValue: '',
    soldFor: '',
    expiresOn: '',
    code: '',
  });

  const openVoucherDialog = () => {
    setVoucherDraft({
      voucherType: 'GIFT_CARD', faceValue: '', soldFor: '', expiresOn: '', code: '',
    });
    setVoucherDialogOpen(true);
  };

  const saveVoucher = async () => {
    const face = num(voucherDraft.faceValue);
    if (face == null || face <= 0) { toast.error('Set the value of the card'); return; }
    setSaving(true);
    try {
      await issueVoucher({
        voucherType: voucherDraft.voucherType,
        faceValue: face,
        // Promotional credit is given, not sold — the server refuses a price on
        // it, so do not send one.
        soldFor: voucherDraft.voucherType === 'GIFT_CARD'
          ? (num(voucherDraft.soldFor) ?? face) : null,
        expiresOn: voucherDraft.expiresOn || null,
        code: voucherDraft.code.trim() || null,
      });
      toast.success('Issued');
      setVoucherDialogOpen(false);
      await loadVouchers();
    } catch (err) {
      toast.error(apiMessage(err, 'Could not issue that'));
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (v: Voucher) => {
    setHistoryFor(v);
    setHistory([]);
    try {
      const { data } = await getVoucherHistory(v.id);
      setHistory(data);
    } catch { toast.error('Could not load the history'); }
  };

  const onDeactivate = async () => {
    if (!confirmDeactivate) return;
    try {
      await deactivateVoucher(confirmDeactivate.id);
      toast.success('Deactivated');
      setConfirmDeactivate(null);
      await loadVouchers();
    } catch { toast.error('Could not deactivate that'); }
  };

  // ── render ────────────────────────────────────────────────────────────

  const summary = tab === 'offers'
    ? [
        { label: 'Live right now', value: String(liveCount), zero: liveCount === 0 },
        { label: 'Switched on', value: String(activeCount), zero: activeCount === 0 },
        { label: 'Offers set up', value: String(promotions.length), zero: promotions.length === 0 },
      ]
    : [
        {
          label: 'In circulation',
          value: String(vouchers.filter(v => v.spendable).length),
          zero: vouchers.filter(v => v.spendable).length === 0,
        },
        { label: 'Value outstanding', value: formatCurrency(liability), zero: liability === 0 },
        { label: 'Issued in total', value: String(vouchers.length), zero: vouchers.length === 0 },
      ];

  return (
    <Box sx={{ p: 3, backgroundColor: 'var(--bg-app)', minHeight: '100vh' }}>
      <PageHeroBand
        eyebrow="Settings · Loyalty Programs"
        title="Loyalty Programs"
        heroLabel={tab === 'offers' ? 'Live right now' : 'Value outstanding'}
        heroValue={tab === 'offers' ? String(liveCount) : formatCurrency(liability)}
        actions={
          <Box sx={{
            display: 'flex', gap: 1, flexShrink: 0,
            '& .MuiButton-root': {
              backgroundColor: 'color-mix(in srgb, var(--band-text) 10%, transparent)',
              color: 'var(--band-text)',
              borderColor: 'color-mix(in srgb, var(--band-text) 25%, transparent)',
              '&:hover': {
                backgroundColor: 'color-mix(in srgb, var(--band-text) 18%, transparent)',
                borderColor: 'color-mix(in srgb, var(--band-text) 40%, transparent)',
              },
            },
          }}>
            <SecondaryButton
              onClick={tab === 'offers' ? openNew : openVoucherDialog}
              startIcon={<PlusIcon className="h-5 w-5" />}
              data-testid={tab === 'offers' ? 'add-offer-button' : 'add-voucher-button'}
              sx={{ fontSize: '0.813rem', px: 1.5 }}
            >
              {tab === 'offers' ? 'New offer' : 'Issue a card'}
            </SecondaryButton>
          </Box>
        }
      />

      {/* Offers and gift cards are both "things a guest can present", but one is
          a rule and the other is stored value, so they get their own list. */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: { xs: 2, sm: 2.5 }, flexWrap: 'wrap' }}>
        {([
          { key: 'offers', label: 'Offers', count: promotions.length },
          { key: 'vouchers', label: 'Gift cards & credit', count: vouchers.length },
        ] as const).map(t => {
          const on = tab === t.key;
          return (
            <Box
              key={t.key}
              role="button"
              tabIndex={0}
              data-testid={`loyalty-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab(t.key); }
              }}
              sx={{
                px: 1.75, py: 0.75, borderRadius: '999px', cursor: 'pointer',
                fontSize: '0.813rem', fontWeight: on ? 700 : 550,
                border: on ? '1px solid var(--action)' : '1px solid var(--border-subtle)',
                backgroundColor: on ? 'var(--bg-selected)' : 'var(--bg-surface)',
                color: on ? 'var(--action)' : 'var(--text-secondary)',
                '&:focus-visible': { outline: '2px solid var(--focus-ring)' },
              }}
            >
              {t.label} {t.count > 0 && <span style={{ opacity: 0.7 }}>({t.count})</span>}
            </Box>
          );
        })}
      </Box>

      {tab === 'offers' && (
        <Paper
          elevation={0}
          data-testid="auto-apply-row"
          sx={{
            ...cardSx,
            mb: { xs: 2, sm: 2.5 },
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontWeight: 650, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
              Apply offers automatically
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'var(--text-secondary)', mt: 0.25 }}>
              {autoApply
                ? 'A live offer comes off the bill by itself. Staff see it before payment either way.'
                : 'Staff are shown the offer at the till and confirm it before it comes off.'}
            </Typography>
          </Box>
          <Switch
            checked={autoApply}
            disabled={savingAutoApply}
            onChange={toggleAutoApply}
            inputProps={{ 'aria-label': 'Apply offers automatically' }}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--action)' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: 'var(--action)',
              },
            }}
          />
        </Paper>
      )}

      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        {summary.map(card => (
          <Grid item xs={4} sm={4} md={4} key={card.label}>
            <Paper elevation={0} sx={cardSx}>
              <Typography variant="caption" sx={{
                color: 'var(--text-tertiary)', fontWeight: 600,
                textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.05em',
              }}>
                {card.label}
              </Typography>
              <Typography sx={{
                // Nothing to report reads quiet, not bold — same rule as Finance.
                fontWeight: card.zero ? 600 : 700,
                color: card.zero ? 'var(--text-tertiary)' : 'var(--text-primary)',
                fontSize: { xs: '1.35rem', sm: '1.75rem' },
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.01em', lineHeight: 1.15, mt: 0.25,
              }}>
                {card.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {tab === 'offers' ? (
        <Paper elevation={0} sx={{
          backgroundColor: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: '12px', overflow: 'hidden',
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: 'var(--action)' }} />
            </Box>
          ) : promotions.length === 0 ? (
            <EmptyState
              // Deliberately the quiet default, not `actionable`. That variant
              // fills the panel with theme.primary — under Aubergine & Saffron
              // that is a slab of saffron the width of the page — and forces the
              // icon white, which the icon's own colour then overrode, leaving
              // saffron on saffron and no icon at all. The icon is left untinted
              // here so EmptyState colours it against its own background.
              icon={<TicketIcon />}
              title="No offers yet"
              subtitle="Happy hours, coupons, stamp cards and birthday treats all start here"
              actionButton={
                <PrimaryButton onClick={openNew} startIcon={<PlusIcon className="h-5 w-5" />}>
                  New offer
                </PrimaryButton>
              }
            />
          ) : (
            promotions.map((p, i) => {
              const k = kindFor(p);
              const Icon = k.icon;
              return (
                <Box
                  key={p.id}
                  data-testid={`offer-row-${p.id}`}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2, p: 2,
                    borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)',
                    backgroundColor: p.liveNow ? 'var(--bg-selected)' : 'transparent',
                  }}
                >
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'var(--bg-sunken)',
                  }}>
                    <Icon style={{ width: 20, height: 20, color: 'var(--action)' }} />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 650, color: 'var(--text-primary)' }}>
                        {p.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={k.label}
                        sx={{
                          height: 20, fontSize: '0.68rem', fontWeight: 600,
                          backgroundColor: 'var(--bg-sunken)', color: 'var(--text-secondary)',
                        }}
                      />
                      {scheduleNote(p) && (
                        <Chip
                          size="small"
                          label={scheduleNote(p)}
                          sx={{
                            height: 20, fontSize: '0.68rem', fontWeight: 600,
                            backgroundColor: 'var(--status-warn-bg)',
                            color: 'var(--status-warn-text)',
                          }}
                        />
                      )}
                      {p.liveNow && (
                        <Chip
                          size="small"
                          label="Live now"
                          sx={{
                            height: 20, fontSize: '0.68rem', fontWeight: 700,
                            backgroundColor: 'var(--status-ok-bg)', color: 'var(--status-ok-text)',
                          }}
                        />
                      )}
                      {!p.isActive && (
                        <Chip
                          size="small"
                          label="Off"
                          sx={{
                            height: 20, fontSize: '0.68rem', fontWeight: 600,
                            backgroundColor: 'var(--bg-sunken)', color: 'var(--text-tertiary)',
                          }}
                        />
                      )}
                    </Box>
                    <Typography sx={{ fontSize: '0.8rem', color: 'var(--text-secondary)', mt: 0.25 }}>
                      {describe(p)}
                    </Typography>
                  </Box>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!p.isActive}
                        onChange={() => onToggle(p)}
                        inputProps={{ 'aria-label': `Switch ${p.name} on or off` }}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--action)' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: 'var(--action)',
                          },
                        }}
                      />
                    }
                    label=""
                    sx={{ mr: 0 }}
                  />
                  <AppIconButton
                    variant="secondary" tooltip="Edit" size="small"
                    icon={<PencilSquareIcon style={{ width: 18, height: 18 }} />}
                    onClick={() => openEdit(p)}
                  />
                  <AppIconButton
                    variant="delete" tooltip="Delete" size="small"
                    icon={<TrashIcon style={{ width: 18, height: 18 }} />}
                    onClick={() => setConfirmDelete(p)}
                  />
                </Box>
              );
            })
          )}
        </Paper>
      ) : (
        <Paper elevation={0} sx={{
          backgroundColor: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: '12px', overflow: 'hidden',
        }}>
          {vouchers.length === 0 ? (
            <EmptyState
              icon={<CreditCardIcon />}
              title="No cards issued"
              subtitle="Sell a gift card, or hand out credit as an apology or a reward"
              actionButton={
                <PrimaryButton onClick={openVoucherDialog} startIcon={<PlusIcon className="h-5 w-5" />}>
                  Issue a card
                </PrimaryButton>
              }
            />
          ) : (
            vouchers.map((v, i) => (
              <Box
                key={v.id}
                data-testid={`voucher-row-${v.id}`}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2, p: 2,
                  borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)',
                }}
              >
                <Box sx={{
                  width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: 'var(--bg-sunken)',
                }}>
                  <CreditCardIcon style={{ width: 20, height: 20, color: 'var(--action)' }} />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{
                      fontWeight: 650, color: 'var(--text-primary)',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}>
                      {v.code}
                    </Typography>
                    <Chip
                      size="small"
                      label={v.voucherType === 'GIFT_CARD' ? 'Gift card' : 'Credit'}
                      sx={{
                        height: 20, fontSize: '0.68rem', fontWeight: 600,
                        backgroundColor: 'var(--bg-sunken)', color: 'var(--text-secondary)',
                      }}
                    />
                    {!v.spendable && (
                      <Chip
                        size="small"
                        label={v.expired ? 'Expired' : !v.isActive ? 'Deactivated' : 'Spent'}
                        sx={{
                          height: 20, fontSize: '0.68rem', fontWeight: 600,
                          backgroundColor: 'var(--bg-sunken)', color: 'var(--text-tertiary)',
                        }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '0.8rem', color: 'var(--text-secondary)', mt: 0.25 }}>
                    {formatCurrency(v.remainingBalance)} left of {formatCurrency(v.initialBalance)}
                    {v.expiresOn ? ` · expires ${v.expiresOn}` : ' · no expiry'}
                    {v.taxTreatment === 'VAT_AT_ISSUE' ? ' · VAT accounted at sale' : ''}
                  </Typography>
                </Box>

                <SecondaryButton
                  onClick={() => openHistory(v)}
                  sx={{ fontSize: '0.75rem', px: 1.25, minWidth: 0 }}
                >
                  History
                </SecondaryButton>
                {v.isActive && (
                  <AppIconButton
                    variant="delete" tooltip="Deactivate" size="small"
                    icon={<TrashIcon style={{ width: 18, height: 18 }} />}
                    onClick={() => setConfirmDeactivate(v)}
                  />
                )}
              </Box>
            ))
          )}
        </Paper>
      )}

      {/* ── new / edit offer ───────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          {step === 'form' && !draft.id && (
            <AppIconButton
              variant="secondary" tooltip="Back" size="small"
              icon={<ArrowLeftIcon style={{ width: 18, height: 18 }} />}
              onClick={() => setStep('pick')}
            />
          )}
          {step === 'pick'
            ? 'What kind of offer?'
            : draft.id ? 'Edit offer' : `New ${kind.label.toLowerCase()}`}
        </DialogTitle>

        <DialogContent>
          {step === 'pick' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
              <Typography sx={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Pick one and only the settings it needs are asked for.
              </Typography>

              <KindGrid
                kinds={KINDS.filter(k => k.common)}
                onPick={chooseKind}
              />

              <Box
                role="button"
                tabIndex={0}
                data-testid="more-kinds-toggle"
                onClick={() => setShowMoreKinds(v => !v)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault(); setShowMoreKinds(v => !v);
                  }
                }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                  color: 'var(--action)', fontWeight: 650, fontSize: '0.85rem',
                  '&:focus-visible': { outline: '2px solid var(--focus-ring)' },
                }}
              >
                <ChevronDownIcon style={{
                  width: 16, height: 16,
                  transform: showMoreKinds ? 'rotate(180deg)' : 'none',
                  transition: 'transform 150ms',
                }} />
                {showMoreKinds ? 'Fewer kinds' : `More kinds (${KINDS.filter(k => !k.common).length})`}
              </Box>

              <Collapse in={showMoreKinds} unmountOnExit>
                <KindGrid kinds={KINDS.filter(k => !k.common)} onPick={chooseKind} />
              </Collapse>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                borderRadius: '10px', backgroundColor: 'var(--bg-selected)',
              }}>
                <kind.icon style={{ width: 22, height: 22, color: 'var(--action)', flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontWeight: 650, fontSize: '0.875rem', color: 'var(--action)' }}>
                    {kind.label}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {kind.blurb}
                  </Typography>
                </Box>
              </Box>

              <TextField
                label="Name"
                placeholder={kind.namePlaceholder}
                value={draft.name}
                onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                fullWidth
                helperText={kind.promoType === 'TIER' ? 'This is the tier name guests will see' : undefined}
              />

              {/* ── what the guest gets ─────────────────────────────── */}
              {(needs('percentOrAmount') || needs('amountOrPercent')) && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    select
                    label={kind.group === 'till' ? 'Takes off' : 'Reward'}
                    value={draft.valueMode}
                    onChange={e => setDraft(d => ({
                      ...d, valueMode: e.target.value as Draft['valueMode'],
                    }))}
                    sx={{ width: 170 }}
                  >
                    <MenuItem value="percentage">A percentage</MenuItem>
                    <MenuItem value="amount">An amount</MenuItem>
                  </TextField>
                  <TextField
                    label={draft.valueMode === 'percentage'
                      ? 'Percent' : `Amount (${CURRENCY.symbol})`}
                    type="number"
                    value={draft.value}
                    onChange={e => setDraft(d => ({ ...d, value: e.target.value }))}
                    fullWidth
                  />
                </Box>
              )}

              {needs('percentOnly') && (
                <TextField
                  label={kind.promoType === 'CASHBACK' ? 'Percent back' : 'Percent'}
                  type="number"
                  value={draft.value}
                  onChange={e => setDraft(d => ({ ...d, value: e.target.value }))}
                  fullWidth
                />
              )}

              {needs('points') && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label={`Points per ${CURRENCY.symbol}1`}
                    type="number"
                    value={draft.pointsPerUnit}
                    onChange={e => setDraft(d => ({ ...d, pointsPerUnit: e.target.value }))}
                    fullWidth
                  />
                  <TextField
                    label={`One point is worth (${CURRENCY.symbol})`}
                    type="number"
                    value={draft.pointsToValue}
                    onChange={e => setDraft(d => ({ ...d, pointsToValue: e.target.value }))}
                    fullWidth
                  />
                </Box>
              )}

              {needs('birthdayWindow') && (
                <TextField
                  select
                  label="How long the treat lasts"
                  value={draft.triggerWindowDays || '15'}
                  onChange={e => setDraft(d => ({ ...d, triggerWindowDays: e.target.value }))}
                  fullWidth
                  helperText="Given once a year, however many times they come in"
                >
                  <MenuItem value="0">On the day itself</MenuItem>
                  <MenuItem value="3">That week</MenuItem>
                  <MenuItem value="15">That month</MenuItem>
                </TextField>
              )}

              {needs('lapsedWindow') && (
                <TextField
                  label="Counts as away after (days)"
                  type="number"
                  value={draft.triggerWindowDays}
                  onChange={e => setDraft(d => ({ ...d, triggerWindowDays: e.target.value }))}
                  fullWidth
                  helperText="How long since their last visit before this offer applies"
                />
              )}

              {needs('minRedeem') && (
                <TextField
                  label={kind.promoType === 'CASHBACK'
                    ? `Smallest amount of credit spendable (${CURRENCY.symbol})`
                    : 'Fewest points spendable at once'}
                  type="number"
                  value={draft.minRedeemPoints}
                  onChange={e => setDraft(d => ({ ...d, minRedeemPoints: e.target.value }))}
                  fullWidth
                  helperText="Leave empty for no floor. Stops a balance trickling away in pennies at the till."
                />
              )}

              {needs('qualifyingItem') && (
                <TextField
                  select
                  label="Which item"
                  value={draft.qualifyingItemId}
                  onChange={e => setDraft(d => ({ ...d, qualifyingItemId: e.target.value }))}
                  fullWidth
                  helperText={menuItems.length === 0 ? 'Add items to your menu first' : undefined}
                >
                  {menuItems.map(m => (
                    <MenuItem key={m.id} value={String(m.id)}>{m.name}</MenuItem>
                  ))}
                </TextField>
              )}

              {needs('itemCount') && (
                <TextField
                  label="How many they buy"
                  type="number"
                  value={draft.requiredItemCount}
                  onChange={e => setDraft(d => ({ ...d, requiredItemCount: e.target.value }))}
                  fullWidth
                  helperText="Buy this many, get one free"
                />
              )}

              {needs('freeItem') && (
                <TextField
                  select
                  label={kind.promoType === 'ITEM_FREE' || kind.promoType === 'BOGO'
                    ? 'Item given free' : 'Or give an item free'}
                  value={draft.freeItemId}
                  onChange={e => setDraft(d => ({ ...d, freeItemId: e.target.value }))}
                  fullWidth
                  helperText={menuItems.length === 0
                    ? 'Add items to your menu first'
                    : kind.promoType === 'ITEM_FREE' || kind.promoType === 'BOGO'
                      ? undefined : 'Optional — instead of a discount'}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {menuItems.map(m => (
                    <MenuItem key={m.id} value={String(m.id)}>{m.name}</MenuItem>
                  ))}
                </TextField>
              )}

              {/* ── what has to happen first ─────────────────────────── */}
              {needs('visits') && (
                <TextField
                  label="Visits needed"
                  type="number"
                  value={draft.requiredVisits}
                  onChange={e => setDraft(d => ({ ...d, requiredVisits: e.target.value }))}
                  fullWidth
                  helperText="Counts completed orders by the same guest"
                />
              )}

              {needs('spendTarget') && (
                <TextField
                  label={`Total spend needed (${CURRENCY.symbol})`}
                  type="number"
                  value={draft.requiredSpend}
                  onChange={e => setDraft(d => ({ ...d, requiredSpend: e.target.value }))}
                  fullWidth
                  helperText={kind.promoType === 'TIER'
                    ? 'Added up across all their visits — the benefit then stays'
                    : 'Added up across all their visits'}
                />
              )}

              {/* ── when it runs ─────────────────────────────────────── */}
              {needs('window') && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="From" type="time" value={draft.startTime}
                    onChange={e => setDraft(d => ({ ...d, startTime: e.target.value }))}
                    InputLabelProps={{ shrink: true }} fullWidth
                  />
                  <TextField
                    label="Until" type="time" value={draft.endTime}
                    onChange={e => setDraft(d => ({ ...d, endTime: e.target.value }))}
                    InputLabelProps={{ shrink: true }} fullWidth
                    helperText="Your restaurant's local time"
                  />
                </Box>
              )}

              {needs('schedule') && (
                <>
                  <Box>
                    <Typography sx={labelSx}>Days — leave empty for every day</Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {DAYS.map(day => {
                        const on = draft.days.includes(day);
                        return (
                          <Chip
                            key={day}
                            label={day}
                            onClick={() => setDraft(d => ({
                              ...d,
                              days: on ? d.days.filter(x => x !== day) : [...d.days, day],
                            }))}
                            sx={{
                              fontWeight: on ? 700 : 500,
                              backgroundColor: on ? 'var(--bg-selected)' : 'var(--bg-sunken)',
                              color: on ? 'var(--action)' : 'var(--text-secondary)',
                              border: on ? '1px solid var(--action)' : '1px solid var(--border-subtle)',
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>

                  <Box>
                    <Typography sx={labelSx}>Where it applies — leave empty for everywhere</Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {CHANNELS.map(c => {
                        const on = draft.channels.includes(c.value);
                        return (
                          <Chip
                            key={c.value}
                            label={c.label}
                            onClick={() => setDraft(d => ({
                              ...d,
                              channels: on
                                ? d.channels.filter(x => x !== c.value)
                                : [...d.channels, c.value],
                            }))}
                            sx={{
                              fontWeight: on ? 700 : 500,
                              backgroundColor: on ? 'var(--bg-selected)' : 'var(--bg-sunken)',
                              color: on ? 'var(--action)' : 'var(--text-secondary)',
                              border: on ? '1px solid var(--action)' : '1px solid var(--border-subtle)',
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Starts on" type="date" value={draft.startsOn}
                  onChange={e => setDraft(d => ({ ...d, startsOn: e.target.value }))}
                  InputLabelProps={{ shrink: true }} fullWidth helperText="Optional"
                />
                <TextField
                  label="Ends on" type="date" value={draft.endsOn}
                  onChange={e => setDraft(d => ({ ...d, endsOn: e.target.value }))}
                  InputLabelProps={{ shrink: true }} fullWidth helperText="Optional"
                />
              </Box>

              {(needs('minSpend') || needs('cap')) && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {needs('minSpend') && (
                    <TextField
                      label={`Minimum spend (${CURRENCY.symbol})`}
                      type="number"
                      value={draft.minOrderAmount}
                      onChange={e => setDraft(d => ({ ...d, minOrderAmount: e.target.value }))}
                      fullWidth helperText="Optional"
                    />
                  )}
                  {needs('cap') && draft.valueMode === 'percentage' && (
                    <TextField
                      label={`Most it can take off (${CURRENCY.symbol})`}
                      type="number"
                      value={draft.maxDiscountAmount}
                      onChange={e => setDraft(d => ({ ...d, maxDiscountAmount: e.target.value }))}
                      fullWidth helperText="Optional"
                    />
                  )}
                </Box>
              )}

              {needs('validity') && (
                <TextField
                  label="Reward stays valid for (days)"
                  type="number"
                  value={draft.rewardValidityDays}
                  onChange={e => setDraft(d => ({ ...d, rewardValidityDays: e.target.value }))}
                  fullWidth
                  helperText={kind.promoType === 'TIER'
                    ? 'Leave empty and the tier never lapses'
                    : 'Leave empty and it never expires'}
                />
              )}

              {/* ── coupon code ──────────────────────────────────────── */}
              {showsCoupon && (
                <Box sx={{
                  p: 1.75, borderRadius: '10px',
                  border: '1px solid var(--border-subtle)',
                  backgroundColor: 'var(--bg-sunken)',
                }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={draft.useCode}
                        onChange={e => setDraft(d => ({ ...d, useCode: e.target.checked }))}
                        inputProps={{ 'aria-label': 'Needs a code' }}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--action)' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: 'var(--action)',
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontWeight: 650, fontSize: '0.875rem' }}>
                          Needs a code
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                          Off means it applies on its own, with nothing to ask for
                        </Typography>
                      </Box>
                    }
                  />
                  <Collapse in={draft.useCode} unmountOnExit>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                      <TextField
                        label="Code"
                        value={draft.code}
                        onChange={e => setDraft(d => ({ ...d, code: e.target.value.toUpperCase() }))}
                        fullWidth
                        inputProps={{ maxLength: 40, style: { textTransform: 'uppercase' } }}
                        helperText="3–40 characters: letters, numbers, hyphen or underscore. Case does not matter to the guest."
                      />
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          label="Total uses" type="number"
                          value={draft.maxRedemptions}
                          onChange={e => setDraft(d => ({ ...d, maxRedemptions: e.target.value }))}
                          fullWidth helperText="Empty = unlimited"
                        />
                        <TextField
                          label="Uses per guest" type="number"
                          value={draft.maxPerCustomer}
                          onChange={e => setDraft(d => ({ ...d, maxPerCustomer: e.target.value }))}
                          fullWidth helperText="Only counts signed-in guests"
                        />
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              )}

              {/* ── advanced ─────────────────────────────────────────── */}
              {needs('advanced') && (
                <Box>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowAdvanced(v => !v)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault(); setShowAdvanced(v => !v);
                      }
                    }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                      color: 'var(--text-secondary)', fontWeight: 600, fontSize: '0.8rem',
                      '&:focus-visible': { outline: '2px solid var(--focus-ring)' },
                    }}
                  >
                    <ChevronDownIcon style={{
                      width: 15, height: 15,
                      transform: showAdvanced ? 'rotate(180deg)' : 'none',
                      transition: 'transform 150ms',
                    }} />
                    When more than one offer could apply
                  </Box>
                  <Collapse in={showAdvanced} unmountOnExit>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={draft.nonExclusive}
                            onChange={e => setDraft(d => ({ ...d, nonExclusive: e.target.checked }))}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--action)' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: 'var(--action)',
                              },
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography sx={{ fontWeight: 650, fontSize: '0.875rem' }}>
                              Can combine with other offers
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                              Off by default — otherwise two offers can stack and the
                              guest gets more than intended
                            </Typography>
                          </Box>
                        }
                      />
                      <TextField
                        label="Order applied" type="number"
                        value={draft.priority}
                        onChange={e => setDraft(d => ({ ...d, priority: e.target.value }))}
                        fullWidth
                        helperText={`Lower goes first. 20% then ${CURRENCY.symbol}100 off is not the same as ${CURRENCY.symbol}100 off then 20%.`}
                      />
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        {step === 'form' && (
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <SecondaryButton onClick={() => setDialogOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton onClick={save} loading={saving}>
              {draft.id ? 'Save' : 'Create offer'}
            </PrimaryButton>
          </DialogActions>
        )}
      </Dialog>

      {/* ── issue a voucher ───────────────────────────────────────── */}
      <Dialog open={voucherDialogOpen} onClose={() => setVoucherDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Issue a card</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              select
              label="Kind"
              value={voucherDraft.voucherType}
              onChange={e => setVoucherDraft(v => ({
                ...v, voucherType: e.target.value as typeof v.voucherType,
              }))}
              fullWidth
            >
              <MenuItem value="GIFT_CARD">Gift card — sold to a customer</MenuItem>
              <MenuItem value="PROMO_CREDIT">Credit — given away</MenuItem>
            </TextField>

            <TextField
              label={`Value (${CURRENCY.symbol})`}
              type="number"
              value={voucherDraft.faceValue}
              onChange={e => setVoucherDraft(v => ({ ...v, faceValue: e.target.value }))}
              fullWidth
              helperText={
                voucherDraft.voucherType === 'GIFT_CARD' && isIndia
                  // RBI caps a prepaid gift instrument at ₹10,000. The server
                  // refuses more; saying so here saves a round trip.
                  ? `An Indian gift voucher cannot exceed ${CURRENCY.symbol}10,000`
                  : 'What the card is worth to spend'
              }
            />

            {voucherDraft.voucherType === 'GIFT_CARD' && (
              <TextField
                label={`Paid (${CURRENCY.symbol})`}
                type="number"
                value={voucherDraft.soldFor}
                onChange={e => setVoucherDraft(v => ({ ...v, soldFor: e.target.value }))}
                fullWidth
                helperText={`Leave empty if they paid the full value. Set it lower for "pay ${CURRENCY.symbol}45, get ${CURRENCY.symbol}50".`}
              />
            )}

            <TextField
              label="Expires on" type="date"
              value={voucherDraft.expiresOn}
              onChange={e => setVoucherDraft(v => ({ ...v, expiresOn: e.target.value }))}
              InputLabelProps={{ shrink: true }} fullWidth
              helperText="Optional. Twelve to twenty-four months is the accepted fair range."
            />

            <TextField
              label="Code"
              value={voucherDraft.code}
              onChange={e => setVoucherDraft(v => ({ ...v, code: e.target.value.toUpperCase() }))}
              fullWidth
              helperText="Leave empty and one is generated — no letters that get misread down a phone"
            />

            {voucherDraft.voucherType === 'GIFT_CARD' && !isIndia && (
              <Box sx={{
                p: 1.5, borderRadius: '10px',
                backgroundColor: 'var(--bg-sunken)',
                border: '1px solid var(--border-subtle)',
              }}>
                <Typography sx={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                  Your menu carries one VAT rate, so this counts as a single-purpose
                  voucher and VAT falls due now, at sale — not when it is spent.
                  The treatment is fixed on the card at issue and will not change later.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <SecondaryButton onClick={() => setVoucherDialogOpen(false)}>Cancel</SecondaryButton>
          <PrimaryButton onClick={saveVoucher} loading={saving}>Issue</PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* ── voucher history ───────────────────────────────────────── */}
      <Dialog open={!!historyFor} onClose={() => setHistoryFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {historyFor?.code}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: 'var(--text-secondary)', mb: 2 }}>
            Every movement, so the balance can always be explained.
          </Typography>
          {history.length === 0 ? (
            <Typography sx={{ color: 'var(--text-tertiary)', py: 2 }}>Nothing recorded yet.</Typography>
          ) : (
            history.map((t, i) => (
              <Box key={i}>
                {i > 0 && <Divider />}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.25 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 650, fontSize: '0.875rem' }}>
                      {t.type === 'ISSUE' ? 'Issued'
                        : t.type === 'REDEEM' ? 'Spent'
                        : t.type === 'REFUND' ? 'Refunded'
                        : t.type === 'EXPIRE' ? 'Expired' : 'Adjusted'}
                      {t.orderId ? ` · order ${t.orderId}` : ''}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                      {t.at ? t.at.replace('T', ' ').slice(0, 16) : ''}
                      {t.notes ? ` · ${t.notes}` : ''}
                    </Typography>
                  </Box>
                  <Typography sx={{
                    fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                    color: 'var(--text-primary)',
                  }}>
                    {formatCurrency(t.amount)}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.78rem', color: 'var(--text-tertiary)',
                    fontVariantNumeric: 'tabular-nums', minWidth: 70, textAlign: 'right',
                  }}>
                    {formatCurrency(t.balanceAfter)} left
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <SecondaryButton onClick={() => setHistoryFor(null)}>Close</SecondaryButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete this offer?"
        message={confirmDelete ? `"${confirmDelete.name}" will be removed.` : ''}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(null)}
      />

      <ConfirmDialog
        open={!!confirmDeactivate}
        title="Deactivate this card?"
        message={confirmDeactivate
          ? `${confirmDeactivate.code} still holds ${formatCurrency(confirmDeactivate.remainingBalance)}. It will stop working, and the balance stays on the record.`
          : ''}
        confirmLabel="Deactivate"
        variant="danger"
        onConfirm={onDeactivate}
        onCancel={() => setConfirmDeactivate(null)}
      />
    </Box>
  );
};

/** The tile grid. Same shape whether it is the common kinds or the rest. */
const KindGrid: React.FC<{
  kinds: OfferKind[];
  onPick: (k: OfferKind) => void;
}> = ({ kinds, onPick }) => {
  const groups: OfferKind['group'][] = ['till', 'earned', 'event'];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {groups.map(g => {
        const inGroup = kinds.filter(k => k.group === g);
        if (inGroup.length === 0) return null;
        return (
          <Box key={g}>
            <Typography sx={labelSx}>{GROUP_LABEL[g]}</Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' },
              gap: 1,
            }}>
              {inGroup.map(k => {
                const Icon = k.icon;
                return (
                  <Box
                    key={k.key}
                    role="button"
                    tabIndex={0}
                    data-testid={`kind-tile-${k.key}`}
                    onClick={() => onPick(k)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(k); }
                    }}
                    sx={{
                      p: 1.5, borderRadius: '10px', cursor: 'pointer',
                      border: '1px solid var(--border-default)',
                      backgroundColor: 'var(--bg-surface)',
                      transition: 'border-color 120ms, background-color 120ms',
                      '&:hover': {
                        borderColor: 'var(--action)',
                        backgroundColor: 'var(--bg-selected)',
                      },
                      '&:focus-visible': { outline: '2px solid var(--focus-ring)' },
                    }}
                  >
                    <Icon style={{ width: 22, height: 22, color: 'var(--action)' }} />
                    <Typography sx={{
                      fontWeight: 650, fontSize: '0.875rem',
                      color: 'var(--text-primary)', mt: 0.75,
                    }}>
                      {k.label}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.72rem', color: 'var(--text-tertiary)',
                      mt: 0.25, lineHeight: 1.35,
                    }}>
                      {k.blurb}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default VendorLoyaltyPrograms;
